import React from 'react';

/** Widget host element for a node inside the shadow root (or the node itself outside one). */
function getHostElement(node: Node): Element | null {
  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;
  return node instanceof Element ? node : null;
}

const POSITIONED = ['relative', 'absolute', 'fixed', 'sticky'];

/**
 * Whether `sibling` paints above the widget, whose branch in the page is
 * `node`. On a z-index tie, CSS paints the later element on top.
 */
function paintsAboveWidget(
  sibling: Element,
  node: Element,
  widgetZIndex: number
): boolean {
  // shortcut: checks the sibling only, so an overlay whose z-index sits on a nested child stays inert; scan descendants if a host page needs it.
  const style = getComputedStyle(sibling);
  // Browsers report a declared z-index even where it has no effect.
  if (!POSITIONED.includes(style.position)) return false;
  const zIndex = Number.parseInt(style.zIndex, 10);
  if (Number.isNaN(zIndex)) return false;
  if (zIndex !== widgetZIndex) return zIndex > widgetZIndex;
  return Boolean(
    node.compareDocumentPosition(sibling) & Node.DOCUMENT_POSITION_FOLLOWING
  );
}

/**
 * While `active`, marks everything in the host page except the path to the
 * widget as `inert`, so a full-screen dialog cannot be left by keyboard or
 * screen-reader navigation. Elements painted above the widget (e.g. a cookie
 * banner) stay usable, since they are visible on top of the dialog. Only the
 * elements this hook marked are restored, so anything the page had already
 * made inert stays that way.
 */
export function useInertHostPage(
  dialogRef: React.RefObject<HTMLElement | null>,
  active: boolean,
  widgetZIndex: number
) {
  React.useEffect(() => {
    if (!active) return;
    const dialog = dialogRef.current;
    const host = dialog ? getHostElement(dialog) : null;
    if (!host) return;

    const added: Element[] = [];
    let node: Element | null = host;
    while (node && node !== document.body) {
      const parent: Element | null = node.parentElement;
      if (!parent) break;
      for (const sibling of Array.from(parent.children)) {
        if (sibling === node) continue;
        if (sibling.hasAttribute('inert')) continue;
        if (paintsAboveWidget(sibling, node, widgetZIndex)) continue;
        sibling.setAttribute('inert', '');
        added.push(sibling);
      }
      node = parent;
    }

    return () => {
      added.forEach(el => el.removeAttribute('inert'));
    };
  }, [dialogRef, active, widgetZIndex]);
}
